import React from 'react';
import ApolloClient from 'apollo-boost';
import { ApolloProvider } from '@apollo/react-hooks';
import Layout from '../../components/layout';
import SEO from '../../components/seo';
import { SettingProvider } from '../../lib/context/settings';
import { AdsProvider } from '../../lib/context/ads';
import { Col, Row, Collapse } from 'antd';
import { Title } from '../../components/styles';
import { device } from '../../lib/theme';
import styled from 'styled-components';

const { Panel } = Collapse;

const HeroContainer = styled(Row)`
  background-size: cover;
  background-position: right;
  background-repeat: no-repeat;
  height: 100px;
  margin: auto;
  h1 {
    max-width: 80%;
    font-size: 32px;
    line-height: 1.1;
  }

  @media ${device.laptop} {
    h1 {
      max-width: 60%;
      font-size: 56px;
      line-height: 1.2;
    }

    background-position: center center;
  }
`;

const client = new ApolloClient<any>({
  uri: `https://${process.env.GATSBY_WORDPRESS_URL_PATH}/graphql`,
  fetchOptions: {
    credentials: 'include',
  },
});

const MembershipFAQs: React.FC<any> = () => {
  return (
    <ApolloProvider client={client as any}>
      <SettingProvider>
        <AdsProvider>
          <Layout key={`${typeof window}`}>
            <SEO
              title="AAA Membership FAQ"
              description="Get answers to your most frequently asked questions about AAA Membership, including roadside assistance and other member benefits."
              canonicalUrl="/membership"
            />

            <HeroContainer align="middle" justify="center">
              <Col xs={{ span: 22, offset: 1 }} md={{ span: 20, offset: 2 }}>
                <Title style={{ margin: 'auto' }}>AAA Membership FAQ</Title>
              </Col>
            </HeroContainer>

            <Row style={{ paddingTop: 48 }}>
              <Col xs={{ span: 22, offset: 1 }} md={{ span: 20, offset: 2 }}>
                <p>
                  Have a question about Membership? Find your answers below.
                </p>
                <Collapse defaultActiveKey={['1']} ghost>
                  <Panel header="HOW DO I REQUEST ROADSIDE ASSISTANCE?" key="1">
                    <p>
                      Call <a href="tel:8002224357">800-AAA-HELP (222-4357)</a>{' '}
                      or request service online at{' '}
                      <a href="https://www.aaa.com/help">AAA.com/Help.</a> You
                      can also download the AAA app and use it to request
                      roadside assistance.
                    </p>
                  </Panel>
                  <Panel header="WHAT DOES MY AAA MEMBERSHIP COVER?" key="2">
                    <ul>
                      <li>Towing Service</li>
                      <li>Tire Service</li>
                      <li>Fuel Delivery Service</li>
                      <li>Battery Service</li>
                      <li>Lockout & Key Service</li>
                      <li>Extrication/Winching Service</li>
                      <li>Trip Interruption Reimbursement</li>
                      <li>Auto/Stereo Theft Protection</li>
                      <li>Legal Defense Reimbursement</li>
                    </ul>
                    <p>
                      Plus, every membership includes: free maps, travel guides,
                      exclusive savings on car repair and auto insurance,
                      thousands of dining, retail and entertainment discounts,
                      and the largest travel agency in the Carolinas are all
                      available to you online, on the phone and in-person.
                      Actual benefits vary by membership service level. To see a
                      comprehensive list of beneﬁts and regulations, please see
                      our{' '}
                      <a href="https://carolinas.aaa.com/membership/guide/">
                        Comprehensive Membership Guide
                      </a>
                      .
                    </p>
                  </Panel>
                  <Panel
                    header="HOW MANY SERVICE CALLS DO I GET PER YEAR?"
                    key="4"
                  >
                    <p>
                      Members receive four service calls each (Primary and
                      Associate Members).
                    </p>
                  </Panel>
                  <Panel header="WHAT IS A PRIMARY MEMBER?" key="5">
                    <p>
                      A Primary Member is the initial holder of the membership
                      in the household who makes the decisions regarding the
                      membership, such as adding associates or cancelling the
                      membership.
                    </p>
                  </Panel>
                  <Panel
                    header="WHAT ARE THE QUALIFICATIONS FOR SOMEONE TO BE
                    AN ASSOCIATE ON MY MEMBERSHIP?"
                    key="6"
                  >
                    <p>
                      Associates may be a spouse or any dependent children age
                      25 and under, living with and supported by the immediate
                      family. Children who reach age 26, leave the household, or
                      become self-supporting are no longer eligible for
                      associate status and require their own individual Primary
                      Membership.
                    </p>
                  </Panel>
                  <Panel
                    header="ARE THERE ANY RESTRICTIONS ON THE TYPES OF VEHICLES COVERED
                    FOR EMERGENCY ROAD SERVICE?"
                    key="7"
                  >
                    <p>
                      For Basic, Plus, and Premier Members, AAA will provide
                      service to the following: Four-wheeled motor driven
                      vehicles of the passenger type regardless of license plate
                      designation, if those services can be safely delivered.
                      Dual-wheel campers/motor homes and RVs will be provided
                      all services except towing, extrication/winching, and tire
                      service, which will be offered only to Plus/RV or
                      Premier/RV Members.
                    </p>
                    <p>
                      Rented passenger vehicles are eligible for service.
                      Unloaded commercial vehicles are eligible for service,
                      excluding taxi cabs, transport/shuttle services and
                      limousines. Dual-wheel unloaded pickup trucks and panel
                      vans (up to ten passengers) must have Plus/RV or
                      Premier/RV coverage to be eligible for towing and are
                      provided all services except changing inner tire.
                    </p>
                    <p>
                      Box trucks are provided all services except towing,
                      winching, and tire service. For Plus/RV and Premier/RV
                      Members only, the Club extends all service to:
                    </p>
                    <p>
                      Motor homes, travel trailers with sleeping accommodations
                      for the purpose of camping only, pickup trucks with
                      bed-mounted campers, motorcycles, dual-wheel unloaded
                      pickup trucks, panel vans, and 5th wheel travel trailers.
                      Some larger trucks and some multi-passenger vehicles may
                      not be covered by AAA Carolinas if determined unsafe by
                      the AAA Road Service Provider based on Department of
                      Transportation guidelines.
                    </p>
                  </Panel>
                  <Panel
                    header="HOW CAN I GET A LIST OF ALL OF THE SERVICES THAT ARE PROVIDED
                    WITH EMERGENCY ROAD SERVICE?"
                    key="8"
                  >
                    <p>
                      You can get a complete list of services in the{' '}
                      <a href="https://carolinas.aaa.com/membership/guide/">
                        AAA Membership Guide
                      </a>
                      .
                    </p>
                  </Panel>
                  <Panel
                    header="IF MY GRANDCHILDREN ARE DRIVING MY VEHICLE ARE THEY COVERED
                    IN AN EMERGENCY UNDER MY MEMBERSHIP?"
                    key="9"
                  >
                    <p>
                      AAA Membership covers the Member, not the vehicle. Service
                      will be provided to the person named on a valid membership
                      card if he or she is driving or riding in an eligible
                      vehicle at the time of breakdown. Prior to rendering
                      service, AAA may request identification, such as a driver
                      license, in addition to the AAA Membership card.
                    </p>
                    <p>
                      Only members may use the membership card and service is
                      not provided to non-members using the member’s vehicle,
                      whether or not they reside in the member’s household.
                      Spouse and children may be added as “Associate” Members
                      which include all services provided to the Primary Member.
                    </p>
                  </Panel>
                  <Panel
                    header="CAN I SCHEDULE AN APPOINTMENT IN ADVANCE TO HAVE MY CAR
                    TOWED TO THE REPAIR SHOP?"
                    key="10"
                  >
                    <p>
                      We do not make future appointments for towing services
                      since they are for emergency road service events where
                      your car is unexpectedly disabled. Towing for routine
                      maintenance or repairs is not a covered service and is
                      considered a convenience service which would be billed at
                      a commercial rate if arranged by AAA Carolinas.
                    </p>
                  </Panel>
                  <Panel
                    header="IF I CAN’T PAY THE FULL AMOUNT OF MY MEMBERSHIP RENEWAL
                    AT ONE TIME CAN I MAKE PAYMENT ARRANGEMENTS OR PAY IN INSTALLMENTS?"
                    key="11"
                  >
                    <p>
                      At this time we do not have any installment payment plans.
                      We accept cash, checks, or credit card payments for
                      membership renewal.
                    </p>
                  </Panel>
                  <Panel
                    header="DOES MY AAA MEMBERSHIP COVER VEHICLE LOCKOUT SERVICE?"
                    key="12"
                  >
                    <p>
                      If your keys are locked inside the vehicle, service will
                      be sent to gain entrance. If your keys are lost, broken,
                      or the service provider cannot gain entrance to your
                      vehicle, locksmith service up to $50.00 will be provided.
                    </p>
                    <p>
                      For the protection of the member’s vehicle, AAA may
                      request ownership verification of the vehicle and/or seek
                      identification of the person driving the vehicle.
                      Registered owners must be present for this service. Plus,
                      Plus/ RV, Premier and Premier/RV provides up to $100 for
                      locksmith service to make your vehicle operable.
                    </p>
                  </Panel>
                  <Panel
                    header="WHAT IF I RUN OUT OF GAS? CAN MY AAA MEMBERSHIP HELP?"
                    key="13"
                  >
                    <p>
                      Yes. An emergency supply of fuel will be delivered to your
                      disabled vehicle to enable you to reach the nearest
                      service station. Specific brands or octane ratings cannot
                      be promised.
                    </p>
                    <p>
                      The charge for the fuel will be the current pump price and
                      is payable to the AAA Contracted Road Service Provider
                      upon delivery. If you need it, you can be towed under the
                      normal towing provisions.
                    </p>
                    <p>
                      With Plus, Plus/RV, Premier, and Premier/RV, we’ll provide
                      a sufficient amount of fuel to enable you to reach the
                      nearest service station at no additional cost.
                    </p>
                  </Panel>
                  <Panel
                    header="I’M A AAA MEMBER FROM ANOTHER STATE. CAN I TRANSFER MY AAA
                    MEMBERSHIP TO NORTH CAROLINA OR SOUTH CAROLINA?"
                    key="14"
                  >
                    <p>
                      Yes! Transferring your membership from another club to AAA
                      Carolinas is fast and easy. You can complete that request
                      online{' '}
                      <a href="https://join.carolinas.aaa.com/transfer/">
                        here
                      </a>{' '}
                      or by calling <a href="tel:18005938626">1-866-593-8626</a>
                      .
                    </p>
                  </Panel>
                  <Panel
                    header="CAN I GIVE A AAA MEMBERSHIP AS A GIFT?"
                    key="15"
                  >
                    <p>
                      Absolutely. Having a AAA membership gives you and those
                      you care about access to more than just our extensive
                      network of road service professionals. This protection
                      covers your friends and loved ones whether they are in
                      their own vehicle, a rental or with a friend.
                    </p>
                    <p>
                      Plus, every membership includes: free maps, travel guides,
                      exclusive savings on car repair and auto insurance, and
                      the largest travel agency in the Carolinas are all
                      available to you online, on the phone and in-person.
                    </p>
                    <p>
                      <a href="https://join.carolinas.aaa.com/login">
                        Give a Gift Membership Online
                      </a>{' '}
                      or call one of our membership advisors at{' '}
                      <a href="tel:18665938626">1-866-593-8626</a>.
                    </p>
                  </Panel>
                  <Panel
                    header="I HAVE ALWAYS HAD A AAA PLUS/RV MEMBERSHIP BUT I DON’T
                    SEE THAT BEING OFFERED ONLINE WHEN I GO TO RENEW. IS IT STILL AVAILABLE?"
                    key="16"
                  >
                    <p>
                      Yes, we still offer the Plus/RV membership. This is not
                      offered online as we prefer our members call so we can
                      ensure you are renewing at the service level that best
                      suits your needs based on driving habits and the types of
                      vehicles you own.
                    </p>
                  </Panel>
                  <Panel
                    header="HOW LONG DOES MY VEHICLE HAVE TO SIT AT A REPAIR SHOP OR
                    HOME BEFORE IT IS NO LONGER CONSIDERED TO BE A ONE TOW PER BREAKDOWN?"
                    key="17"
                  >
                    <p>
                      We are here to get you and your vehicle to a safe
                      location. Regardless of how long a vehicle sits at a
                      repair shop/home, towing a vehicle away from the same
                      location is not covered because the vehicle has already
                      been towed to a safe location.
                    </p>
                  </Panel>
                  <Panel
                    header="WHY DOESN’T MY EXPIRATION DATE CHANGE TO END ON THE MONTH
                    THAT I REINSTATED MY MEMBERSHIP?"
                    key="18"
                  >
                    <p>
                      Expiration dates are determined by when payment is made
                      and the status of the membership account is at the time of
                      payment.
                    </p>
                  </Panel>
                  <Panel
                    header="WHY ARE NEW MEMBER DISCOUNTS NOT OFFERED TO EXISTING
                    MEMBERS?"
                    key="19"
                  >
                    <p>
                      We have specific criteria we use to offer new member
                      discounts, however there are many discounts for membership
                      available to existing members.
                    </p>
                    <p>
                      For example, as of April 2, 2019, AAA Members that enroll
                      in Auto Renew and have their credit card billed
                      automatically each year receive a discount of $5 per
                      membership year. If a member cancels Auto Renew, they will
                      no longer receive the discount.
                    </p>
                  </Panel>
                  <Panel
                    header="WHY IS THERE A WAIT FOR PLUS MEMBERSHIP FOR NEW MEMBERS?"
                    key="20"
                  >
                    <p>
                      The waiting period was put in place to help reduce misuse
                      of the extended benefits that Plus members receive.
                    </p>
                  </Panel>
                  <Panel
                    header="WHY DOES AAA REQUIRE MEMBERS WITH TRUCKS LARGER THAN AN
                    F250 (2500), HAVE AN RV MEMBERSHIP?"
                    key="21"
                  >
                    <p>
                      In an effort to provide a safe environment for our members
                      and providers, large trucks and some multi-passenger
                      vehicles, including but not limited to: F-350s, F-450s,
                      motorcycles, dual-wheel trucks, Hummers and vehicles
                      extending past the rollback bed, must have Plus/RV or
                      Premier/RV coverage or they will not be covered by AAA
                      Carolinas.
                    </p>
                    <p>
                      In combination with the total weight and type of the tow
                      vehicle, these restrictions are required as different
                      equipment is needed to provide service. This restriction
                      is based on: the weight of the vehicle being towed, weight
                      distribution of the vehicle being towed and weight loads
                      on the axle of the tow vehicle, and safety based on
                      Department of Transportation guidelines.
                    </p>
                    <p>
                      Note: The final decision to provide service to a vehicle
                      safely is left to the discretion of the AAA Contracted
                      Road Service Provider.
                    </p>
                  </Panel>
                  <Panel
                    header="WHY DO I HAVE TO HAVE VALID TAGS ON MY VEHICLE IF AAA
                    IS TOWING IT?"
                    key="22"
                  >
                    <p>
                      AAA Roadside Service is for emergency use when a vehicle
                      unexpectedly becomes disabled and should not be used for
                      maintenance. Therefore we require that your vehicle have
                      valid tags and registration.
                    </p>
                    <p>
                      AAA will make an exception for vehicles with tags not
                      expired more than 30 days and/or vehicles without tags or
                      displaying an invalid tag if a police report is present.
                    </p>
                  </Panel>
                  <Panel
                    header="WHY DO I HAVE TO SHOW A VALID PHOTO ID? I’VE NEVER BEEN TOLD
                    THIS BEFORE."
                    key="23"
                  >
                    <p>
                      AAA membership covers the members listed on the membership
                      and not the vehicle. Therefore we require a valid photo ID
                      for the member at the time of service. It also helps to
                      ensure that member benefits are being used appropriately.
                      This safeguards our members from someone else using their
                      membership without their knowledge.
                    </p>
                  </Panel>
                  <Panel
                    header="WHAT DOES THE TRIP INTERRUPTION COVER?"
                    key="24"
                  >
                    <p>
                      AAA Members have added peace of mind when on trips of 100
                      driving miles or more from home. If you are driving or
                      riding in a AAA-qualifying vehicle which becomes
                      inoperable as a result of a traffic accident or if your
                      vehicle is stolen, (or mechanical breakdown, Premier
                      Members only) and causes your trip to be delayed at least
                      24 hours, you may receive a reimbursement covering
                      unexpected out-of-pocket expenses incurred during the
                      first 72 hours (96 hours for Premier Members) immediately
                      following the incident.
                    </p>
                    <p>These expenses include:</p>
                    <ul>
                      <li>
                        Car rental (excluding gas, insurance, and drop or one
                        way fees)
                      </li>
                      <li>
                        Meals and lodging in the vicinity of the incident
                        (within a 25 mile radius)
                      </li>
                    </ul>
                    <p>OR</p>
                    <ul>
                      <li>
                        Commercial transportation from the location of the
                        incident to your destination or current residence
                      </li>
                      <li>
                        Reimbursement coverage varies by membership service
                        level and includes:
                      </li>
                      <li>Basic: Up to $250</li>
                      <li>Plus and Plus/RV: Up to $500</li>
                      <li>Premier and Premier/RV: Up to 1500</li>
                    </ul>
                    <p>
                      <small>
                        **The trip interruption benefit can be used once per
                        membership, per membership year. An accident report must
                        be filled with the local police department and submitted
                        to the club along with all other required documentation
                        within 60 days of the incident. For more information on
                        reimbursement guidelines, documents and to file a claim,
                        contact the Member Relations department at{' '}
                        <a href="tel:8772823682">877-282-3682</a>.
                      </small>
                    </p>
                  </Panel>
                  <Panel
                    header="WHAT IS THE “ONE TOW PER BREAKDOWN” POLICY?"
                    key="25"
                  >
                    <p>
                      We are here to get you and your vehicle to a safe location
                      when your vehicle unexpectedly becomes disabled. Once your
                      vehicle has been towed to a safe location, the situation
                      is no longer considered an emergency and towing the
                      vehicle to another location becomes the member’s expense.
                    </p>
                  </Panel>
                </Collapse>
              </Col>
            </Row>
          </Layout>
        </AdsProvider>
      </SettingProvider>
    </ApolloProvider>
  );
};

export default MembershipFAQs;
